/**
 * In Vite dev, API calls target the stack's Fastify port (VITE_API_ORIGIN or inferred
 * from the UI port / gateway mount). Production and same-origin deploys keep relative /api.
 *
 * On the HTTPS gateway (`https://localhost:5173/5178/…`) never use `http://localhost:8127`
 * — Chrome blocks that as mixed content and the hub stays on a blank/connecting gate.
 */
import { joinHubLiveStreamUrl, resolvedHubLiveStreamOrigin } from "./hubLiveStreamOrigin";

import { devStackMountPrefixFromPathname } from "./devStackMount";
import { expectedDevApiPort, shouldUseDevViteApiProxy } from "./hubDevApiBinding";

export type HubDevLocation = {
  hostname: string;
  port: string;
  pathname: string;
};

/**
 * Same-origin prefix for stack API calls (`/5178`) when the page is the gateway
 * path mount or the direct stack Vite origin that already uses that base.
 */
export function hubDevSameOriginApiPrefix(location: HubDevLocation | null | undefined): string | null {
  if (!location) {
    return null;
  }
  if (location.hostname !== "localhost" && location.hostname !== "127.0.0.1") {
    return null;
  }
  const mount = devStackMountPrefixFromPathname(location.pathname);
  if (!mount) {
    return null;
  }
  if (location.port === "5173" || location.port === "" || location.port === mount.slice(1)) {
    return mount;
  }
  return null;
}

export function applyHubDevSameOriginApiPrefix(path: string, prefix: string | null): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (!prefix) {
    return normalized;
  }
  if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
    return normalized;
  }
  return `${prefix}${normalized}`;
}

function currentDevSameOriginApiPrefix(): string | null {
  if (!import.meta.env?.DEV || typeof window === "undefined") {
    return null;
  }
  return hubDevSameOriginApiPrefix(window.location);
}

/** Direct Fastify origin for this stack (bypasses the Vite /api proxy). */
export function hubDirectDevApiOrigin(): string {
  const fromEnv = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, "");
  }
  return `http://localhost:${expectedDevApiPort()}`;
}

export function hubDevApiOrigin(): string | null {
  if (!import.meta.env?.DEV) {
    return null;
  }
  if (currentDevSameOriginApiPrefix()) {
    return null;
  }
  if (shouldUseDevViteApiProxy()) {
    return null;
  }
  const fromEnv = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, "");
  }
  return `http://localhost:${expectedDevApiPort()}`;
}

export function hubApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const mount = currentDevSameOriginApiPrefix();
  if (mount) {
    return applyHubDevSameOriginApiPrefix(path, mount);
  }
  const origin = hubDevApiOrigin();
  if (!origin) {
    if (!path.startsWith("/")) {
      return `/${path}`;
    }
    return path;
  }
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Long-lived SSE. Same origin as REST unless a live origin is configured (build-time
 * VITE_HUB_LIVE_ORIGIN or runtime GET /api/hub/public-config) so the document host
 * is not Chrome-coalesced onto the HTTP/2 session that carries REST.
 * 503 responses during tsx watch reloads are retryable in the stream client.
 */
export function hubLiveStreamApiUrl(path: string): string {
  return joinHubLiveStreamUrl(resolvedHubLiveStreamOrigin(), path, hubApiUrl(path));
}

/** Dev restart banner polls the routed stack API, not gateway /healthz (primary 8123). */
export function hubDevStackHealthzUrl(): string {
  return hubApiUrl("/healthz");
}
